import { Injectable } from '@angular/core';
import { DynamicItemsConfigEntry } from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import {
  isRecommendationsPanelConfigItemWithConditions,
  RecommendationsPanelConfigItem,
} from '@epicuro-next/models/recommendation-panels';
import {
  selectBasketNotificationStyle,
  selectRecommendationsPanelsRule,
} from '@epicuro-next/platform/configuration';
import {
  clearPane,
  PaneLocation,
  setPane,
} from '@epicuro-next/state/user-ui-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BasketNotificationStyle } from 'libs/constants/rule-engine/pulse-rules/src/lib/types/basket-notification-style';
import { filter, map, withLatestFrom } from 'rxjs';

import { clearBasketNotification, setBasketNotification } from '../actions';

/**
 * Basket state side effects
 */
@Injectable()
export class BasketDataEffects {
  private readonly basketNotificationPaneLocation: PaneLocation = 'right';

  private readonly basketNotificationPaneContent$ = this.store
    .select(selectRecommendationsPanelsRule)
    .pipe(
      map((recommendationsPanels) => {
        return recommendationsPanels.AddedToBasketNotificationPane;
      }),
    );

  private readonly basketNotificationStyle$ = this.store.select(
    selectBasketNotificationStyle,
  );

  public readonly showBasketNotificationInPane$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setBasketNotification),
      withLatestFrom(
        this.basketNotificationStyle$,
        this.basketNotificationPaneContent$,
      ),
      map(([, basketNotificationStyle, paneContent]) => {
        return {
          basketNotificationStyle,
          paneContent,
        };
      }),
      filter(({ basketNotificationStyle }) => {
        return basketNotificationStyle === BasketNotificationStyle.insidePane;
      }),
      map(({ paneContent }) => {
        const items = this.getDynamicItemsConfigEntries(paneContent);

        const actionToDispatch = setPane({
          location: this.basketNotificationPaneLocation,
          content: items,
        });

        return actionToDispatch;
      }),
    );
  });

  public readonly clearBasketNotificationPane$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(clearBasketNotification),
      withLatestFrom(this.basketNotificationStyle$),
      filter(([, basketNotificationStyle]) => {
        return basketNotificationStyle === BasketNotificationStyle.insidePane;
      }),
      map(() => {
        return clearPane({
          location: this.basketNotificationPaneLocation,
        });
      }),
    );
  });

  constructor(private actions$: Actions, private store: Store) {}

  private getDynamicItemsConfigEntries(
    paneContent: RecommendationsPanelConfigItem,
  ): DynamicItemsConfigEntry[] {
    const contentForPane = Array.isArray(paneContent)
      ? paneContent
      : [paneContent];

    const items: DynamicItemsConfigEntry[] = [];
    for (const contentItem of contentForPane) {
      if (isRecommendationsPanelConfigItemWithConditions(contentItem)) {
        console.warn(
          `Conditions are not supported for basket notification pane`,
        );

        for (const item of contentItem.items) {
          items.push(item);
        }
      } else {
        items.push(contentItem);
      }
    }
    return items;
  }
}
